// Wrestling-specific taxonomy + constants for the Combatives Detailed Journal.
// Drafted per /app/memory/WRESTLING_RESEARCH.md, founder-approved.
//
// SCOPE: hobbyist / scholastic / collegiate / masters wrestlers + MMA
// cross-trainers. NOT designed for elite-international-level granularity —
// free-text fields cover edge cases.
//
// STYLES UNIFIED: Folkstyle / Freestyle / Greco-Roman share one technique
// tree with a session-level `style` field. UI hides style-irrelevant blocks
// (e.g., leg attacks for greco, riding time for freestyle/greco, push-outs
// for folkstyle).
//
// CITED SOURCES (per founder direction — credibility/defensibility):
//   - USA Wrestling Coaches Manual & Rulebook (latest published edition)
//     https://www.themat.com/page/show/2070234-rulebooks
//   - NCAA Wrestling Rules (current edition)
//     https://www.ncaapublications.com/p-4682-2024-26-ncaa-wrestling-rules.aspx
//   - UWW (United World Wrestling) International Wrestling Rules
//     https://uww.org/sites/default/files/2024-02/wrestling_rules.pdf
//   - FILA / UWW technique encyclopedia
//   - "Wrestle Like a Champion" (Aldrich), "Wrestling Tough" (Chapman),
//     "Coaching Wrestling Successfully" (Cary Kolat) — referenced
//     terminology cross-checks.
// Citations are inline as `// src:` annotations on terminology that's
// non-obvious or carries scoring/rule meaning.

export const WRESTLING_STYLES = [
  {
    id: 'folkstyle',
    label: 'Folkstyle',
    description: 'US scholastic/collegiate. Riding time, near-falls, pin emphasis.',
    legAttacks: true,
    ridingTime: true,           // src: NCAA Rule 5
    pushOut: false,
    nearFallPts: [2, 3, 4],     // src: NCAA Rule 6
  },
  {
    id: 'freestyle',
    label: 'Freestyle',
    description: 'International / Olympic. Throws, exposures, push-outs.',
    legAttacks: true,
    ridingTime: false,
    pushOut: true,              // src: UWW International Rules Art. 49
    exposurePts: [2, 4],        // src: UWW Art. 47
  },
  {
    id: 'greco-roman',
    label: 'Greco-Roman',
    description: 'Olympic / international. Upper body only — no leg attacks.',
    legAttacks: false,          // src: UWW Art. 38 (Greco-Roman prohibitions)
    ridingTime: false,
    pushOut: true,
    exposurePts: [2, 4],
    upperBodyOnly: true,
  },
];

export const WRESTLING_CLASS_FORMATS = [
  { id: 'drilling',      label: 'Drilling' },
  { id: 'live',          label: 'Going Live' },
  { id: 'situational',   label: 'Situational' },
  { id: 'conditioning',  label: 'Conditioning' },
  { id: 'weight_work',   label: 'Strength / Lift' },
  { id: 'match_dual',    label: 'Match / Dual' },
  { id: 'tournament',    label: 'Tournament' },
  { id: 'film_scouting', label: 'Film / Scouting' },
  { id: 'weight_cut',    label: 'Weight-cut day' },
  { id: 'open_mat',      label: 'Open mat' },
];

// Mirrors BJJ/Judo vocabulary intentionally — eases the shared
// <GrapplingJournalDialog> refactor later.
export const WRESTLING_FOCUS_MODES = [
  'Drilling',
  'Going Live',
  'Mastery',
  'Conditioning',
  'Recovery',
];

// Focus modes that should reveal the Sparring section by default.
export const WRESTLING_SPARRING_TRIGGERS = ['Going Live'];

// Per-technique self-evaluation. Identical structure to BJJ_MASTERY_LEVELS;
// labels lightly re-worded for wrestling vernacular.
export const WRESTLING_MASTERY_LEVELS = [
  { value: 'Studying',    label: 'Studying',    help: "Watched the technique, haven't drilled it" },
  { value: 'Drilling',    label: 'Drilling',    help: "Can do it slow with a willing partner" },
  { value: 'Working',     label: 'Working',     help: "Hitting it sometimes in live wrestling" },
  { value: 'Hitting Live', label: 'Hitting Live', help: "Landing it consistently in live" },
  { value: 'Default Move', label: 'Default Move', help: "Reflexive — your go-to in that phase" },
];

// Anti-shame felt scale — matches BJJ shape so the shared refactor reuses it.
export const WRESTLING_FELT_SCALE = [
  { value: 1, label: 'Got smashed' },
  { value: 2, label: 'Held my own' },
  { value: 3, label: 'Even' },
  { value: 4, label: 'Pushed the pace' },
  { value: 5, label: 'Dominated' },
];

// Three phases of action. Same shape as BJJ playerSide, different labels.
// src: USA Wrestling Coaches Manual + NCAA Rule 3 (positions).
export const WRESTLING_PHASES = [
  { id: 'neutral', label: 'Neutral (on feet)' },
  { id: 'top',     label: 'Top' },
  { id: 'bottom',  label: 'Bottom' },
];

// Three-level technique tree:
//   phase ('neutral' | 'top' | 'bottom') → subgroup → technique name
// Tagged with style filter where relevant. Free-text `name` allowed for
// anything outside the curated list (the row stores `name` regardless).
export const WRESTLING_TECHNIQUES = [
  // ============ NEUTRAL ============
  {
    phase: 'neutral',
    subgroup: 'Stance & Motion',
    techniques: [
      'Square stance', 'Staggered stance', 'Level change', 'Penetration step',
      'Forward pressure', 'Lateral motion', 'Hand fighting',
    ],
  },
  {
    phase: 'neutral',
    subgroup: 'Setups',
    techniques: [
      'Snap-down', 'Shrug', 'Arm drag', 'Post-and-pull', 'Inside tie',
      'Russian tie', 'Two-on-one', 'Collar tie', 'Pummel', 'Underhook battle',
    ],
  },
  {
    phase: 'neutral',
    subgroup: 'Leg Attacks',
    styleRestrict: ['folkstyle', 'freestyle'], // hide for greco
    techniques: [
      'Single leg (high crotch)', 'Single leg (low)', 'Single leg (sweep)',
      'Single leg (lift)', 'Double leg', 'High crotch', 'Ankle pick',
      "Fireman's carry", 'Low single', 'Sweep single',
    ],
  },
  {
    phase: 'neutral',
    subgroup: 'Upper-Body Throws',
    techniques: [
      'Headlock', 'Lateral drop', 'Bear hug', 'Body lock', 'Suplex (back arch)',
      'Souplesse', 'Salto', 'Slide-by',
    ],
  },
  {
    phase: 'neutral',
    subgroup: 'Defense — vs leg attack',
    styleRestrict: ['folkstyle', 'freestyle'],
    techniques: [
      'Sprawl', 'Whizzer', 'Front headlock (collar/elbow)', 'Underhook on shot',
      'Crossface', 'Snap down on shot', 'Re-shot',
    ],
  },
  {
    phase: 'neutral',
    subgroup: 'Defense — vs upper body',
    techniques: [
      'Pummel-in', 'Inside position', 'Crossface', 'Tight elbows',
      'Step-out', 'Hip-to-hip block',
    ],
  },

  // ============ TOP ============
  {
    phase: 'top',
    subgroup: 'Rides',
    techniques: [
      'Spiral ride', 'Leg ride', 'Cross-body ride', 'Tight waist + ankle',
      'Two-on-one wrist ride',
    ],
  },
  {
    phase: 'top',
    subgroup: 'Turns / Breakdowns',
    techniques: [
      'Half nelson', 'Bar arm', 'Three-quarter nelson', 'Power half',
      'Crossface cradle', 'Near-side cradle', 'Far-side cradle',
      'Tilt (banana / cement mixer)', 'Turk',
    ],
  },
  {
    phase: 'top',
    subgroup: 'Pinning Combinations',
    techniques: [
      'Pinning half', 'Bar-arm-and-half', 'Cradle pin', 'Reverse half',
      'Side headlock pin',
    ],
  },
  {
    phase: 'top',
    subgroup: 'Gut-Wrench / Lace',
    styleRestrict: ['freestyle', 'greco-roman'],
    techniques: [
      'Gut wrench (basic)', 'Reverse gut', 'Leg lace (ankle lace)',
    ],
  },

  // ============ BOTTOM ============
  {
    phase: 'bottom',
    subgroup: 'Stand-Up Series',
    techniques: [
      'Hand-control stand-up', 'Hip-out stand-up', 'Wall stand-up',
      'Inside leg stand-up',
    ],
  },
  {
    phase: 'bottom',
    subgroup: 'Sit-Outs',
    techniques: [
      'Long sit-out', 'Short sit-out', 'Sit-out-and-turn-in',
    ],
  },
  {
    phase: 'bottom',
    subgroup: 'Switches & Reversals',
    techniques: ['Switch', 'Re-switch', 'Hip heist', 'Granby roll'],
  },
  {
    phase: 'bottom',
    subgroup: 'Defensive (bottom)',
    techniques: ['Hip down', 'Belly-down survival', 'Wrist control', 'Build-up base'],
  },
];

// Helper: filter the technique tree by selected style.
export function wrestlingTechniquesForStyle(styleId) {
  return WRESTLING_TECHNIQUES.filter((g) => !g.styleRestrict || g.styleRestrict.includes(styleId));
}

// Coach-feedback topic chips (used as quick-add pre-populated tags; user
// can still free-text the topic name).
export const WRESTLING_COACH_TOPICS = [
  'Stance', 'Hand fighting', 'Shots / setups', 'Top control', 'Bottom escapes',
  'Conditioning', 'Mental', 'Weight', 'Match strategy', 'Other',
];

// Match outcome — founder decision: W/L/Draw only. Granular major/tech can
// go in the free-text result note. Keeps the form light for the 98%.
export const WRESTLING_MATCH_RESULTS = [
  { id: 'win',  label: 'Win' },
  { id: 'loss', label: 'Loss' },
  { id: 'draw', label: 'Draw' },
];

// Weight-log support — every constant here is in the service of anti-shame
// guardrails. See WRESTLING_RESEARCH.md §7 + founder approval.

export const WRESTLING_CUT_METHODS = [
  { id: 'gradual',     label: 'Gradual diet',     help: 'Calorie deficit over weeks — safest' },
  { id: 'water_cycle', label: 'Water cycling',    help: 'Loading/cutting water in the 72hr window' },
  { id: 'sweat',       label: 'Sweat (sauna / mat / runs)', help: 'Acute water loss in 24hr window' },
  { id: 'mixed',       label: 'Mixed methods',    help: 'Combination of the above' },
];

export const WRESTLING_HYDRATION = [
  { id: 'green',  label: 'Green',  help: 'Urine pale, energy normal, no cramping' },
  { id: 'amber',  label: 'Amber',  help: 'Urine darker, mild thirst, watching closely' },
  { id: 'red',    label: 'Red',    help: 'Dark urine, headache, cramping — STOP cutting' },
];

// Soft-flag thresholds — used by the dialog to surface anti-shame nudges.
// NEVER block the user; ALWAYS frame as care, never as judgment.
// src: NCAA-recommended safe cut guidelines (1.5% body weight per week max,
//      per NCAA Wrestling Weight Management Program). 3lb/day is roughly
//      1.5%/week for a 140lb wrestler — anything faster triggers the soft
//      flag.
export const WRESTLING_WEIGHT_FLAGS = {
  // Day-over-day drop that surfaces the "big drop today — how are you
  // feeling?" prompt. Founder approved.
  rapidDropLbsPerDay: 3,

  // Projected drop > this % of current body weight by weigh-in triggers
  // the "this is a steep cut — make sure your coach is in the loop"
  // prompt. NCAA published guideline is 1.5%/week max.
  riskyProjectedCutPct: 5,

  // Hydration: red ALWAYS prompts. No threshold; the user explicitly
  // self-reported they're in trouble.
};

// Educational reference links — surfaced as tooltips inside the weight
// block. Founder posture: "we're a notebook, not a doctor" — these links
// exist so the user can self-educate from authoritative sources, not so
// we appear to be giving medical guidance.
export const WRESTLING_WEIGHT_REFERENCES = [
  {
    label: 'NCAA Wrestling Weight Management Program',
    url: 'https://www.ncaa.org/sports/2014/12/11/wrestling-weight-management-program.aspx',
    blurb: 'Official NCAA cut-rate guidelines (1.5% body weight per week max).',
  },
  {
    label: 'USA Wrestling: Healthy Weight Management',
    url: 'https://www.teamusa.com/usa-wrestling/resources/coaches-development',
    blurb: 'USA Wrestling-published nutrition and weight guidance for athletes.',
  },
];
